// Branded, print-ready export of the Review Offers comparison.
// Opens a self-contained document in a new tab and triggers the browser's
// print dialog, where "Save as PDF" produces the deliverable. No PDF library
// needed, and the output is fully CSS-controlled so it reads as a First Turn
// document (these often go in front of the client).
import { formatDealValue } from "@/lib/formatDealValue";

export interface OfferPdfRow {
  rank: number;
  buyerName: string;
  firmType: string | null;
  passed: boolean;
  multiple: string;
  ev: { low: number; mid: number; high: number };
  cash: { low: number; mid: number; high: number };
  rolloverPct: number | null;
  rolloverMid: number;
  earnoutAmount: number | null;
  earnoutInEv: boolean | null;
  totalMid: number | null;
  sellerFinancing: number | null;
  received: string | null;
  notes: string | null;
}

export interface OffersPdfOptions {
  clientName: string;
  stage: "ioi" | "loi";
  ebitda: number | null;
  rows: OfferPdfRow[];
  /** Absolute URL of the FTC logo (light-background version). */
  logoUrl?: string;
}

const money = (n: number) => formatDealValue(String(Math.round(n)));
const esc = (s: string) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
const dt = (v: string | null) =>
  v ? new Date(v).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";

function pointCells(v: { low: number; mid: number; high: number }, cls: string): string {
  if (v.low === v.high) {
    return `<td colspan="3" class="num single group-start ${cls}">${money(v.mid)}</td>`;
  }
  return [v.low, v.mid, v.high]
    .map((p, i) => `<td class="num ${cls}${i === 1 ? " mid" : ""}${i === 0 ? " group-start" : ""}">${money(p)}</td>`)
    .join("");
}

export function buildOffersHtml(opts: OffersPdfOptions): string {
  const { clientName, stage, ebitda, rows, logoUrl } = opts;
  const stageLabel = stage === "ioi" ? "Indications of Interest" : "Letters of Intent";
  const today = new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });

  const bodyRows = rows.map((r) => `
    <tr class="${r.passed ? "passed" : ""}">
      <td class="rank"><span class="rank-badge${r.rank === 1 && !r.passed ? " top" : ""}">${r.rank}</span></td>
      <td class="buyer">
        <span class="buyer-name">${esc(r.buyerName)}</span>${r.passed ? '<span class="tag-passed">Passed</span>' : ""}
        ${r.firmType ? `<span class="buyer-sub">${esc(r.firmType)}</span>` : ""}
      </td>
      <td class="mult">${esc(r.multiple)}</td>
      ${pointCells(r.ev, "ev")}
      ${pointCells(r.cash, "cash")}
      <td class="num group-start">${r.rolloverPct != null ? `<span class="strong">${r.rolloverPct}%</span><span class="sub">${money(r.rolloverMid)} mid</span>` : '<span class="none">—</span>'}</td>
      <td class="num">${
        r.earnoutAmount != null && r.earnoutAmount > 0
          ? `<span class="strong">${money(r.earnoutAmount)}</span> <span class="pill">${r.earnoutInEv ? "in EV" : "on top"}</span>${r.totalMid != null ? `<span class="sub">${money(r.totalMid)} total mid</span>` : ""}`
          : '<span class="none">—</span>'
      }</td>
      <td class="num">${r.sellerFinancing != null && r.sellerFinancing > 0 ? money(r.sellerFinancing) : '<span class="none">—</span>'}</td>
      <td class="center">${dt(r.received)}</td>
      <td class="notes">${r.notes ? esc(r.notes) : ""}</td>
    </tr>`).join("");

  const brand = logoUrl
    ? `<img class="logo" src="${esc(logoUrl)}" alt="First Turn Capital">`
    : `<div class="wordmark">FIRST TURN CAPITAL<small>INVESTMENT BANK AND M&amp;A</small></div>`;

  return `<!doctype html>
<html>
<head>
<meta charset="utf-8">
<title>${esc(clientName)} - Review of Offers (${stage.toUpperCase()})</title>
<style>
  @page { size: letter landscape; margin: 12mm 11mm; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  html, body { background: #fff; }
  body { font: 10.5px/1.5 "Segoe UI", "Helvetica Neue", Arial, sans-serif; color: #1e293b; }

  header { display: flex; justify-content: space-between; align-items: center; padding-bottom: 14px; margin-bottom: 16px; border-bottom: 2.5px solid #b8963e; }
  .logo { height: 58px; width: auto; }
  .wordmark { font-family: Georgia, "Times New Roman", serif; font-size: 20px; letter-spacing: 3px; color: #16233b; font-weight: 700; }
  .wordmark small { display: block; font-family: "Segoe UI", Arial, sans-serif; font-size: 8px; letter-spacing: 3.5px; color: #b8963e; font-weight: 600; margin-top: 2px; }
  .title { text-align: right; }
  .title h1 { font-family: Georgia, "Times New Roman", serif; font-size: 21px; color: #16233b; font-weight: 700; letter-spacing: 0.3px; }
  .title .stage { font-size: 11px; color: #b8963e; font-weight: 700; letter-spacing: 1.6px; text-transform: uppercase; margin-top: 3px; }
  .title .meta { font-size: 10px; color: #64748b; margin-top: 4px; }
  .title .meta b { color: #334155; font-weight: 600; }

  table { width: 100%; border-collapse: collapse; }
  thead tr.groups th { background: #16233b; color: #fff; font-size: 8.5px; text-transform: uppercase; letter-spacing: 1.2px; padding: 6px 8px 5px; font-weight: 700; text-align: center; }
  thead tr.groups th.group { border-left: 1px solid #2c3f63; border-right: 1px solid #2c3f63; }
  thead tr.cols th { background: #16233b; color: #b9c5da; padding: 2px 8px 7px; font-size: 7.5px; font-weight: 600; letter-spacing: 0.8px; text-transform: uppercase; text-align: center; }
  thead th.left { text-align: left !important; } thead th.right { text-align: right !important; }
  tbody td { padding: 8px 8px; border-bottom: 1px solid #e5e9f0; vertical-align: middle; }
  tbody tr:nth-child(even) td { background: #f7f9fc; }
  tbody tr { page-break-inside: avoid; }

  .rank { width: 26px; text-align: center; }
  .rank-badge { display: inline-block; min-width: 16px; height: 16px; line-height: 16px; border-radius: 50%; background: #eef1f6; color: #64748b; font-size: 8.5px; font-weight: 700; text-align: center; }
  .rank-badge.top { background: #b8963e; color: #fff; }
  .buyer { min-width: 130px; }
  .buyer-name { display: block; font-weight: 700; color: #0f172a; font-size: 11px; }
  .buyer-sub { display: block; color: #94a3b8; font-size: 8.5px; margin-top: 1px; }
  .tag-passed { display: inline-block; margin-left: 5px; font-size: 7.5px; font-weight: 700; color: #b91c1c; background: #fef2f2; border: 1px solid #fecaca; border-radius: 3px; padding: 0 4px; vertical-align: 2px; }
  .mult { text-align: center; font-weight: 700; color: #16233b; white-space: nowrap; font-size: 11.5px; font-variant-numeric: tabular-nums; }
  .num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; color: #334155; }
  .num.mid { font-weight: 700; color: #0f172a; }
  .num.single { text-align: center; font-weight: 700; color: #0f172a; }
  .cash { color: #166534; } .cash.mid, .cash.single { color: #14532d; }
  .group-start { border-left: 1px solid #dbe2ec; }
  .strong { font-weight: 700; color: #0f172a; }
  .sub { display: block; color: #94a3b8; font-size: 8px; font-weight: 400; margin-top: 1px; }
  .pill { font-size: 7.5px; font-weight: 700; color: #6d28d9; background: #f5f3ff; border: 1px solid #ddd6fe; border-radius: 3px; padding: 0 4px; }
  .none { color: #cbd5e1; }
  .center { text-align: center; color: #64748b; white-space: nowrap; font-size: 9.5px; }
  .notes { color: #475569; font-size: 9px; line-height: 1.4; min-width: 110px; max-width: 170px; }
  tr.passed td { opacity: 0.5; }

  footer { margin-top: 14px; padding-top: 10px; border-top: 1px solid #e5e9f0; display: flex; justify-content: space-between; gap: 32px; font-size: 8px; color: #94a3b8; line-height: 1.5; }
  footer .note { max-width: 68%; }
  footer .conf { text-align: right; color: #64748b; font-weight: 600; }
  footer .conf span { display: block; font-weight: 400; color: #94a3b8; }

  @media print { .no-print { display: none; } }
  .no-print { margin: 18px auto 8px; text-align: center; }
  .no-print button { font: 600 13px "Segoe UI", Arial, sans-serif; background: #16233b; color: #fff; border: 0; border-radius: 6px; padding: 9px 20px; cursor: pointer; }
</style>
</head>
<body>
  <header>
    ${brand}
    <div class="title">
      <h1>Review of Offers</h1>
      <div class="stage">${stageLabel}</div>
      <div class="meta"><b>${esc(clientName)}</b> &nbsp;·&nbsp; ${rows.length} offer${rows.length === 1 ? "" : "s"}${ebitda != null ? ` &nbsp;·&nbsp; EBITDA <b>${money(ebitda)}</b>` : ""} &nbsp;·&nbsp; ${today}</div>
    </div>
  </header>

  <table>
    <thead>
      <tr class="groups">
        <th></th><th></th><th></th>
        <th colspan="3" class="group">Enterprise Value</th>
        <th colspan="3" class="group">Cash at Closing</th>
        <th></th><th></th><th></th><th></th><th></th>
      </tr>
      <tr class="cols">
        <th></th>
        <th class="left">Buyer</th>
        <th>Multiple</th>
        <th class="right">Low</th><th class="right">Mid</th><th class="right">High</th>
        <th class="right">Low</th><th class="right">Mid</th><th class="right">High</th>
        <th>Rollover</th>
        <th>Earnout</th>
        <th>Seller Fin.</th>
        <th>Received</th>
        <th class="left">Notes</th>
      </tr>
    </thead>
    <tbody>${bodyRows}</tbody>
  </table>

  <footer>
    <div class="note">Enterprise value derives from each offer's multiple applied to current EBITDA${ebitda != null ? ` (${money(ebitda)})` : ""}. Cash at closing equals enterprise value less rollover, seller financing, and any earnout included in enterprise value. Figures are indicative and subject to final documentation.</div>
    <div class="conf">Confidential<span>Prepared by First Turn Capital &nbsp;·&nbsp; ${today}</span></div>
  </footer>

  <div class="no-print">
    <button onclick="window.print()">Print / Save as PDF</button>
  </div>

  <script>window.addEventListener("load", function () { setTimeout(function () { window.print(); }, 300); });</script>
</body>
</html>`;
}

export function exportOffersPdf(opts: OffersPdfOptions): void {
  // Blob URL instead of document.write: same-origin document, no injection
  // surface beyond the esc()'d strings above, and plays nice with printing.
  const blob = new Blob([buildOffersHtml(opts)], { type: "text/html" });
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank");
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
